import React, { useState, useMemo } from 'react';
import { useCartContext } from './CartContext';
import { CartLine } from '@interfaces/cart';
import Panel from '@components/molecules/Panel';
import CartItem from './CartItem';
import Button from '@components/atoms/Button';
import CartBodyHeader from './CartBodyHeader';
import CartOrderSummary from './CartOrderSummary';
import CartRecommendations from './CartRecommendations';
import CartBodySkeleton from './CartBodySkeleton';
import CartHoldTimer from './CartHoldTimer';
import PitcoinLoginPrompt from './PitcoinLoginPrompt';
import GiftTier from './GiftTier';
import { generateTrackingItem } from '@helpers/trackCartActions';

const CartBodyContent: React.FC = () => {
  const { cart, data, loading } = useCartContext();

  const [hasHoldTimer, setHasHoldTimer] = useState(false);
  const [isPitcoinVisible, setIsPitcoinVisible] = useState(false);

  // Dynamically determine the max-height value
  const maxHeightClass = useMemo(() => {
    if (data.isCartPage) return 'md:px-0'; // For cart page
    if (hasHoldTimer && isPitcoinVisible) return 'max-h-[calc(100vh-39.6rem)]'; // Both components are visible
    if (hasHoldTimer) return 'max-h-[calc(100vh-36.7rem)]'; // Only CartHoldTimer is visible
    if (isPitcoinVisible) return 'max-h-[calc(100vh-20rem)]'; // Only PitcoinLoginPrompt is visible
    return 'max-h-[calc(100vh-18rem)] lg:max-h-[calc(100vh-14rem)]'; // Default
  }, [data.isCartPage, hasHoldTimer, isPitcoinVisible]);

  const isCartEmpty = useMemo(
    () => !cart.state.lines || cart.state.lines.length === 0,
    [cart.state.lines]
  );

  if (loading) {
    return <CartBodySkeleton isCartPage={data.isCartPage} />;
  }

  return (
    <div
      className={`h-full flex flex-col mb-8 ${
        data.isCartPage ? 'md:flex-row' : ''
      }`}
    >
      <div className={`flex-1 flex flex-col ${data.isCartPage ? 'md:pr-8' : ''}`}>
        <CartBodyHeader
          isCartPage={data.isCartPage}
          isCartEmpty={isCartEmpty}
        />
        {isCartEmpty ? (
          <div className="p-5 flex-1 flex flex-col items-center justify-center gap-5">
            <h3 className="text-center">{data.emptyCartCopy}</h3>
            {data.emptyCartCtaText && data.emptyCartCtaUrl && (
              <Button
                mode="link"
                url={data.emptyCartCtaUrl}
                buttonText={data.emptyCartCtaText}
              />
            )}
          </div>
        ) : (
          <>
            <CartHoldTimer
              holdMessage={data.cartHoldMessage}
              holdEndTime={data.cartHoldEndTime}
              onRemainingTimeUpdate={setHasHoldTimer}
              isCartPage={data.isCartPage}
            />
            <div
              className={`flex-1 flex flex-col overflow-y-auto overflow-x-hidden px-2.5 ${maxHeightClass}`}
            >
              <GiftTier
                subtotal={cart.state.subtotal || { amount: '0', currencyCode: '' }}
              />
              {cart.state.lines?.map((line: CartLine) => (
                <CartItem key={line.id} line={line} />
              ))}
              <CartRecommendations isCartPage={data.isCartPage} />
            </div>

          </>
        )}
      </div>
      {!isCartEmpty && (
        <CartOrderSummary
          isCartPage={data.isCartPage}
          subtotal={cart.state.subtotal || { amount: '0', currencyCode: '' }}
          totalDiscount={
            cart.state.totalDiscount || {
              amount: '0',
              currencyCode: window.DEPT.localization.countryCode,
            }
          }
        />
      )}
      {!data.isCartPage && !isCartEmpty && data.loyaltyEnabled && (
        <PitcoinLoginPrompt
          isCustomerLoggedIn={data.customerLoggedIn}
          subtotal={cart.state?.subtotal || { amount: '0', currencyCode: '' }}
          onVisibilityChange={(isVisible) => setIsPitcoinVisible(isVisible)}
        />
      )}
    </div>
  );
};

const CartBody: React.FC = () => {
  const { data, cart } = useCartContext();

  // Check if cart is opened via a redirect
  const isCartRedirect = useMemo(
    () => !!new URLSearchParams(window.location.search).get('cart'),
    []
  );

  const items = useMemo(() => {
    if (cart.state.lines?.length) {
      return cart.state.lines.map((line: CartLine) => generateTrackingItem(line));
    }
    return [];
  }, [cart.state.lines]);

  // Panel triggers for opening/closing the cart
  const [panelTriggers] = useState<{
    openTriggerEl: HTMLElement | null;
    toggleEl: HTMLElement | null;
  }>({
    openTriggerEl: document.querySelector('[data-header-cart-trigger]'),
    toggleEl: document.querySelector('[data-header-cart-trigger-mobile]'),
  });

  if (data.isCartPage) {
    return <CartBodyContent />;
  }

  return (
    <Panel
      overlay
      triggers={panelTriggers}
      defaultOpen={isCartRedirect}
      context="cart"
      items={items}
    >
      <CartBodyContent />
    </Panel>
  );
};

export default CartBody;
